'use client'

import React from 'react'
import { reportClientError } from '@/lib/logging/clientErrorReporter'

interface RegionalMarketErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

type RegionalMarketFallbackComponent = React.ComponentType<{
  error: Error | null
  retry: () => void
}>

interface RegionalMarketErrorBoundaryProps {
  children: React.ReactNode
  /**
   * Identifies which regional market guard threw the error so the New Relic
   * log / server log can distinguish e.g. the storefront currency guard from
   * the checkout host guard.
   */
  source?: string
  fallback?: RegionalMarketFallbackComponent
}

const DEFAULT_SOURCE = 'regional_market_error_boundary'

const SilentFallback: RegionalMarketFallbackComponent = () => null

/**
 * Error boundary specifically for regional market components.
 * Fails silently visually (does not disrupt browsing/checkout flow) but
 * reports the underlying error to New Relic + server logs with enough
 * context (source, error message, stack, componentStack) to diagnose it.
 */
export class RegionalMarketErrorBoundary extends React.Component<
  RegionalMarketErrorBoundaryProps,
  RegionalMarketErrorBoundaryState
> {
  constructor(props: RegionalMarketErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): RegionalMarketErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const source = this.props.source || DEFAULT_SOURCE

    reportClientError(error, source, {
      boundary: 'regional_market',
      componentStack: errorInfo.componentStack ?? undefined,
      errorName: error.name,
      pageUrl:
        typeof window !== 'undefined' ? window.location.href : undefined,
    })
  }

  retry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || SilentFallback
      return <FallbackComponent error={this.state.error} retry={this.retry} />
    }

    return this.props.children
  }
}

type WithRegionalMarketErrorBoundaryOptions = {
  source?: string
  fallback?: RegionalMarketFallbackComponent
}

/**
 * Higher-order component that wraps components in RegionalMarketErrorBoundary.
 *
 * @example
 * export const MyGuard = withRegionalMarketErrorBoundary(MyGuardImpl, {
 *   source: 'my_guard',
 * })
 */
export function withRegionalMarketErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options: WithRegionalMarketErrorBoundaryOptions = {}
) {
  const { source, fallback } = options

  const WrappedComponent = (props: P) => (
    <RegionalMarketErrorBoundary source={source} fallback={fallback}>
      <Component {...props} />
    </RegionalMarketErrorBoundary>
  )

  WrappedComponent.displayName = `withRegionalMarketErrorBoundary(${
    Component.displayName || Component.name || 'Component'
  })`

  return WrappedComponent
}
